import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { authService } from '@/services/auth/authService';
import { isSimulationActive, isDraftRulesIncluded, setLastResult } from './simulationStore';
import { getLanguageLocale } from './locale';
import type { SimulationResultDto } from '@/types/core/Simulation';

/**
 * Thrown when a request was intercepted by simulation mode.
 * Callers can check `error instanceof SimulationCompleteError` to distinguish
 * from real errors and silently ignore it (the modal handles display).
 */
export class SimulationCompleteError extends Error {
  public readonly result: SimulationResultDto;
  constructor(result: SimulationResultDto) {
    super('Simulation completed');
    this.name = 'SimulationCompleteError';
    this.result = result;
  }
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5050';

// Create axios instance with default config
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000, // 30 seconds — fail fast on network issues instead of hanging
  headers: {
    'Content-Type': 'application/json',
  },
});

// ─── Offline guard ────────────────────────────────────────────────────────────
// Wrap mutating methods so they reject immediately when offline.
// This is more reliable than a request interceptor rejection, which may not
// propagate correctly through axios's internal promise chain in all versions.
function rejectIfOffline(): void {
  if (!navigator.onLine) {
    throw new AxiosError(
      'Network error — your changes are preserved. Check your connection and try saving again.',
      'ERR_NETWORK',
    );
  }
}

const _origPost = apiClient.post.bind(apiClient);
const _origPut = apiClient.put.bind(apiClient);
const _origPatch = apiClient.patch.bind(apiClient);
const _origDelete = apiClient.delete.bind(apiClient);

apiClient.post = function (...args: Parameters<typeof _origPost>) {
  rejectIfOffline();
  return _origPost(...args);
} as typeof _origPost;

apiClient.put = function (...args: Parameters<typeof _origPut>) {
  rejectIfOffline();
  return _origPut(...args);
} as typeof _origPut;

apiClient.patch = function (...args: Parameters<typeof _origPatch>) {
  rejectIfOffline();
  return _origPatch(...args);
} as typeof _origPatch;

apiClient.delete = function (...args: Parameters<typeof _origDelete>) {
  rejectIfOffline();
  return _origDelete(...args);
} as typeof _origDelete;

/** Request configs carry the retry/refresh bookkeeping the response interceptor needs. */
type RetriableRequest = InternalAxiosRequestConfig & {
  /** This request already went through the 401 recovery path once. */
  _retry?: boolean;
  /** This request is the retry issued after a successful token refresh. */
  _refreshed?: boolean;
};

// ─── Refresh circuit breaker ────────────────────────────────────────────────────
// A token refresh is meant to fix a 401. If the freshly minted token is STILL
// rejected, refreshing again only hammers /connect/token. Bound it: count refresh
// attempts in a sliding window and, once too many happen without a refresh
// actually curing a request, stop refreshing for the rest of the window and let
// the 401 surface.
//
// The reset is deliberately narrow: only a request that succeeded ON ITS RETRY
// AFTER a refresh proves the refresh worked. Resetting on any 2xx made the
// breaker unreachable for a partially-authorized user, whose traffic interleaves
// 200s from self-service endpoints with denials — the counter was cleared before
// it could ever reach the limit (item 12 Finding B, measured 2026-08-03).
const REFRESH_BURST_LIMIT = 3;
const REFRESH_BURST_WINDOW_MS = 10_000;
let refreshAttemptTimes: number[] = [];

function refreshBreakerOpen(): boolean {
  const now = Date.now();
  refreshAttemptTimes = refreshAttemptTimes.filter((t) => now - t < REFRESH_BURST_WINDOW_MS);
  return refreshAttemptTimes.length >= REFRESH_BURST_LIMIT;
}

function recordRefreshAttempt(): void {
  refreshAttemptTimes.push(Date.now());
}

function resetRefreshBreaker(): void {
  refreshAttemptTimes = [];
}

// Request interceptor to add auth token, tenant header, and legal entity header
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Abort in-flight mutation requests immediately when the browser goes offline.
    // This handles the case where the user clicks Save while online, then loses network.
    const method = config.method?.toLowerCase();
    if (method && ['post', 'put', 'patch', 'delete'].includes(method)) {
      const controller = new AbortController();
      const onOffline = () => {
        console.warn('[apiClient] Browser went offline — aborting in-flight mutation');
        controller.abort('Device went offline');
      };
      window.addEventListener('offline', onOffline, { once: true });

      // Merge with any existing signal
      const existingSignal = config.signal;
      if (existingSignal) {
        existingSignal.addEventListener?.('abort', () => controller.abort());
      }
      config.signal = controller.signal;

      // Store cleanup so response interceptor can remove the listener
      (config as any)._offlineCleanup = () => window.removeEventListener('offline', onOffline);
    }

    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add tenant header if available
    const tenantId = authService.getTenantId();
    if (tenantId) {
      config.headers['__tenant'] = tenantId;
    }

    // Add Legal Entity header for scope derivation
    // Backend uses this to set Scope.LeId on created entities
    const legalEntityId = authService.getLegalEntityId();
    if (legalEntityId) {
      config.headers['X-Nexo-Legal-Entity-Id'] = legalEntityId;
    }

    // Set Accept-Language so ABP returns localized error messages. This follows the display
    // language, not the number/date format locale (which can differ).
    config.headers['Accept-Language'] = getLanguageLocale();

    // Add simulation headers when simulation mode is active
    if (isSimulationActive()) {
      config.headers['X-Simulation-Mode'] = 'true';
      if (isDraftRulesIncluded()) {
        config.headers['X-Simulation-Include-Drafts'] = 'true';
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
apiClient.interceptors.response.use(
  (response) => {
    // Clean up offline event listener
    (response.config as any)?._offlineCleanup?.();

    // Only a request that succeeded on its post-refresh retry proves the refresh
    // did its job — that, and nothing else, clears the breaker (see the note above).
    if ((response.config as RetriableRequest)?._refreshed) {
      resetRefreshBreaker();
    }

    // Detect simulation results by checking response body (headers may not be CORS-exposed)
    if (response.data?.isSimulation === true) {
      const simulationResult = response.data as SimulationResultDto;
      setLastResult(simulationResult);
      // Reject so callers don't run their normal success handlers (no mutation actually happened)
      return Promise.reject(new SimulationCompleteError(simulationResult));
    }
    return response;
  },
  async (error: AxiosError | SimulationCompleteError) => {
    // Clean up offline event listener
    (error as AxiosError)?.config && ((error as AxiosError).config as any)?._offlineCleanup?.();

    // Let simulation errors pass through without any error handling
    if (error instanceof SimulationCompleteError) {
      return Promise.reject(error);
    }

    // Handle canceled requests (from offline abort) — don't redirect, just propagate
    if (axios.isCancel(error)) {
      console.warn('[apiClient] Request canceled (offline abort)');
      return Promise.reject(new AxiosError(
        'Network error — your changes are preserved. Check your connection and try saving again.',
        'ERR_NETWORK',
      ));
    }

    const originalRequest = (error as AxiosError).config as RetriableRequest;

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Impersonated sessions have no refresh token — a 401 means the
      // short-lived impersonation token expired (or was rejected). Under D13b a
      // full reload drops the memory-only impersonation family and the silent
      // round restores the ORIGINAL identity from the auth-host cookie; a reload
      // also resets the React Query cache holding impersonated data.
      if (authService.isImpersonating()) {
        await authService.endImpersonation();
        authService.clearSilentAttempt();
        window.location.assign('/');
        return Promise.reject(error);
      }

      // Circuit breaker: if refreshing keeps failing to cure the 401 (token
      // rejected even when fresh), stop hammering /connect/token — let this 401
      // surface instead of refreshing or reloading into a loop.
      if (refreshBreakerOpen()) {
        console.warn(
          '[apiClient] refresh circuit breaker open — token still rejected after refresh; ' +
            'surfacing this 401 instead of calling /connect/token again',
        );
        return Promise.reject(error);
      }

      // Single-flight refresh via the rotating refresh token (D13b). Returns null
      // on failure WITHOUT ending the cookie session.
      recordRefreshAttempt();
      const newToken = await authService.refreshToken();
      if (newToken && originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        originalRequest._refreshed = true;
        return apiClient(originalRequest);
      }

      // Refresh could not renew (family dead / no refresh token, or the session hit
      // one of its D7 bounds). Hand it to the session-ended listeners instead of
      // navigating: reloading here is what silently discarded a half-filled form, and
      // with an idle bound in force this stopped being a rare event. SessionExpiredDialog
      // asks, then starts the interactive round with the current view as its return URL.
      authService.clearSilentAttempt();
      authService.notifySessionEnded();
      return Promise.reject(error);
    }

    // 403 Forbidden is NOT refreshed. A 403 from ABP authorization means the
    // caller lacks the permission — a freshly minted token carries the same
    // (empty or insufficient) permission set, so the retry is denied identically.
    // This branch used to refresh "to pick up permission changes"; measured on a
    // real zero-role Entra user it produced 91 /connect/token calls in ~26 min
    // against 4 for an admin doing comparable work, because every denial —
    // multiplied by React Query's retries, which build a fresh config each time
    // and so bypass the `_retry` guard — bought another round trip that could
    // never help (item 12 Finding B, 2026-08-03).
    //
    // Genuine permission changes are still picked up: authService's 5-minute
    // auto-refresh timer exists for exactly that, and it costs one call per user
    // per five minutes instead of one per denial.

    // Handle 500 Internal Server Error
    if (error.response?.status === 500) {
      console.error('Server error:', error);
      // You could show a toast notification here
    }

    // Handle network errors (no response).
    // Two possible causes:
    //   1. User lost connectivity (navigator.onLine === false) — do NOT redirect,
    //      just let the error propagate so the form's onError keeps the user's data.
    //   2. CORS blocking a backend redirect to /Account/Login when the session
    //      has expired — redirect to login.
    if (
      !error.response &&
      (error as AxiosError).code === 'ERR_NETWORK' &&
      authService.getToken()
    ) {
      if (!navigator.onLine) {
        console.warn('[apiClient] Network error — device is offline, not redirecting');
        return Promise.reject(error);
      }

      // Online but got a network error → likely CORS-blocked session redirect.
      // Attempt a lightweight connectivity probe before assuming session expiry.
      try {
        await fetch(`${API_BASE_URL}/api/abp/application-configuration`, {
          method: 'HEAD',
          mode: 'no-cors',
          cache: 'no-store',
        });
        // Probe succeeded — server is reachable, so the original error was
        // likely a transient glitch. Don't redirect, let the caller retry.
        console.warn('[apiClient] Network error but server reachable — not redirecting');
        return Promise.reject(error);
      } catch {
        // Probe also failed while navigator.onLine is true — likely
        // CORS-blocked redirect or server truly down. Recover with a clean
        // authorization-code round rather than ending the cookie session: reload
        // so the boot silent round re-auths, or lands on interactive login.
        console.warn('[apiClient] Network error with existing token — session likely expired, re-authenticating');
        authService.clearSilentAttempt();
        window.location.assign('/');
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

/** Request never reached the server (offline, backend down, CORS). */
export function isNetworkError(error: unknown): boolean {
  return axios.isAxiosError(error) && !error.response && error.code === 'ERR_NETWORK';
}

/** Request was aborted client-side (timeout or cancellation). */
export function isTimeoutError(error: unknown): boolean {
  return axios.isAxiosError(error) && (error.code === 'ECONNABORTED' || error.code === 'ERR_CANCELED');
}

/**
 * Axios error bodies arrive as Blob when the request used `responseType: 'blob'` — parse the JSON
 * back out so getErrorMessage/getValidationErrors see the ABP error payload instead of an opaque
 * Blob (which would silently degrade to the generic status-based message).
 */
export async function normalizeBlobError(error: unknown): Promise<unknown> {
  const response = (error as { response?: { data?: unknown } })?.response;
  if (response?.data instanceof Blob) {
    try {
      response.data = JSON.parse(await response.data.text());
    } catch {
      // Not JSON — leave as-is; getErrorMessage falls back to status-based text.
    }
  }
  return error;
}

/**
 * Error message extractor for user-friendly messages.
 *
 * The network/timeout texts are written for FORM contexts ("try saving
 * again") — callers in other contexts (e.g. the assistant chat) should
 * branch on isNetworkError/isTimeoutError and phrase their own message.
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof SimulationCompleteError) {
    return 'Simulation completed — see results in the simulation panel.';
  }

  if (axios.isAxiosError(error)) {
    // Network error with no response — likely offline
    if (!error.response && error.code === 'ERR_NETWORK') {
      return 'Network error — your changes are preserved. Check your connection and try saving again.';
    }

    // Request timed out or canceled (offline abort)
    if (error.code === 'ECONNABORTED' || error.code === 'ERR_CANCELED') {
      return 'Network error — your changes are preserved. Check your connection and try saving again.';
    }

    const errorData = error.response?.data?.error;

    // For errors with detailed messages in data.message (like MissingRequiredFields, InvalidStateTransition)
    // prefer the detailed message over the generic one
    if (errorData?.data?.message) {
      return errorData.data.message;
    }
    
    // ABP error response format - use generic message if no detailed message available
    if (errorData?.message) {
      return errorData.message;
    }
    
    // Standard error message
    if (error.response?.data?.message) {
      return error.response.data.message;
    }

    // HTTP status messages
    switch (error.response?.status) {
      case 400:
        return 'Invalid request. Please check your input.';
      case 401:
        return 'You are not authenticated. Please login.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 500:
        return 'An internal server error occurred. Please try again later.';
      default:
        return error.message || 'An unexpected error occurred.';
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unknown error occurred.';
}

/**
 * Extract validation errors from Axios error response
 * Re-exported from validationErrors module for convenience
 */
export { getValidationErrors } from './validationErrors';

